// Builds the "Download booking reference" PDF from a submitted booking.
// The low-level PDF drawing lives in utils/pdfWriter.js.

import { parseTimeSlotStartHour, formatTimeSlotRange, getCourtLabel, describeBookingFee } from './bookingUtils';
import { formatMoney } from './currencyUtil';
import { getPaymentBreakdown, describePartialPaymentTag } from './partialPaymentUtil';
import { isValidHex, mixHex } from './colorUtil';
import {
  isPackageBooking,
  getPackageBookingCourts,
  getPackageBookingPrice,
  describePackageBookingTime,
} from './packageUtils';
import {
  DEFAULT_THEME_COLORS,
  GUEST_STATUS_COLORS,
  PAYMENT_SUMMARY_TOTAL_LABEL,
  PAYMENT_SUMMARY_PAID_LABEL,
  PAYMENT_SUMMARY_BALANCE_LABEL,
  BOOKING_REFERENCE_NOTE_TITLE,
  BOOKING_REFERENCE_NOTE_MESSAGE,
  BOOKING_REFERENCE_NOTE_COLORS,
} from '../constants/bookingConst';
import { PdfPage, pageToPdfBlob, textWidthPt, wrapText, hexToPdfColor, loadImageAsJpeg } from './pdfWriter';

const DOW_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatShortDate(dateStr) {
  // dateStr is 'yyyy-MM-dd'
  const [y, m, d] = String(dateStr).split('-').map(Number);
  if (!y || !m || !d) return dateStr || '';
  const dt = new Date(y, m - 1, d);
  return `${DOW_SHORT[dt.getDay()]} ${MON_SHORT[dt.getMonth()]} ${d}`;
}

/**
 * Picks a configured theme colour, falling back when it's missing/malformed
 * (same guard ConfigContext uses before injecting theme CSS).
 * @param {unknown} value
 * @param {string} fallback - "#aabbcc"
 * @returns {string}
 */
const pickHex = (value, fallback) => (isValidHex(value) ? value.trim() : fallback);

/**
 * Builds the "Download booking reference" PDF: a header styled like the
 * site's top bar (Config -> Theme colours + round logo, no nav links), the
 * guest/contact/reference block, a date/court/time/amount table, a total
 * row, an "Important note" disclaimer and a footer line.
 * @param {object} bookingData - the submitted booking
 * @param {object} config - live club config
 * @param {Function} getCourtRate - bookingUtils.getCourtRate
 * @param {{bytes: Uint8Array, width: number, height: number} | null} [logo] - from loadImageAsJpeg()
 * @returns {Blob} application/pdf
 */
export function buildBookingReferencePdfBlob(bookingData, config, getCourtRate, logo = null) {
  const width = 612, height = 792; // US Letter, points
  const page = new PdfPage(width, height);

  const GREEN = [0.106, 0.29, 0.204];
  const GREEN_LIGHT = [0.914, 0.953, 0.929];
  const GREEN_MUTED = [0.42, 0.55, 0.48];
  const INK = [0.157, 0.157, 0.157];
  const INK_SOFT = [0.4, 0.4, 0.4];
  const LINE = [0.85, 0.85, 0.85];
  const BADGE_BG = [0.902, 0.878, 0.973];
  const BADGE_TEXT = [0.36, 0.2, 0.6];
  const WHITE = [1, 1, 1];

  const courtName = config?.courtName || 'Your Court Name';
  const marginX = 40;
  const rightX = width - marginX;

  // ---- Header bar ----
  // Mirrors the site header (.lp-header in LandingPage.css): the configured
  // header background, the round club logo, and the court name in the
  // "Court Name / Logo Color". Nav links are deliberately left out.
  const headerBgHex = pickHex(config?.themeHeaderBackgroundColor, DEFAULT_THEME_COLORS.headerBackground);
  const brandHex = pickHex(config?.themeBrandColor, DEFAULT_THEME_COLORS.brand);
  const headerH = 78;
  page.rect(0, 0, width, headerH, hexToPdfColor(headerBgHex));
  // The site separates the header with a hairline; on a white header it's
  // the only thing marking where the bar ends.
  page.line(0, headerH, width, headerH, hexToPdfColor(mixHex(headerBgHex, brandHex, 0.15)), 0.75);

  let brandX = marginX;
  if (logo) {
    const logoR = 21;
    const logoCx = marginX + logoR;
    const logoCy = headerH / 2;
    // Flat stand-in for BrandLogo's blurred glow: a darker disc sitting a
    // little lower, so the mark lifts off the bar like it does on the site.
    page.circle(logoCx, logoCy + 2.5, logoR + 0.5, hexToPdfColor(mixHex(headerBgHex, '#000000', 0.3)));
    page.circleImage(logoCx, logoCy, logoR, logo);
    brandX = marginX + logoR * 2 + 12;
  }
  page.text(brandX, 39, courtName, { size: 20, bold: true, color: hexToPdfColor(brandHex) });
  page.text(brandX, 56, 'Court booking confirmation', { size: 10, color: hexToPdfColor(mixHex(brandHex, headerBgHex, 0.25)) });

  // ---- Guest / contact ----
  page.text(marginX, 108, 'GUEST NAME', { size: 8.5, bold: true, color: GREEN_MUTED });
  page.text(marginX, 128, bookingData.userName || '-', { size: 13, bold: true, color: INK });
  page.text(width / 2 + 10, 108, 'CONTACT NUMBER', { size: 8.5, bold: true, color: GREEN_MUTED });
  page.text(width / 2 + 10, 128, bookingData.phone || '-', { size: 13, bold: true, color: INK });

  // ---- Booking reference ----
  page.text(marginX, 158, 'BOOKING REFERENCE', { size: 8.5, bold: true, color: GREEN_MUTED });
  page.text(marginX, 181, bookingData.confirmationId || '-', { size: 17, bold: true, color: INK });

  page.line(marginX, 198, rightX, 198, LINE, 1);

  // ---- Table ----
  // Multi-date bookings carry a `dates` array instead of just one
  // date/timeSlots pair (see App.jsx's createPendingBooking) — fall back to
  // treating the single fields as a one-entry list for an ordinary
  // single-date booking. Each row already carries its own `date`, so
  // looping over every date group here is all this table needs.
  const rows = [];
  const dateGroups = Array.isArray(bookingData.dates) && bookingData.dates.length > 0
    ? bookingData.dates
    : [{ date: bookingData.date, timeSlots: bookingData.timeSlots }];

  if (isPackageBooking(bookingData)) {
    // One line for the whole block. Itemising a package by the hour would
    // print hourly court rates the guest was never charged, across hours that
    // include the club's cleanup slot — and the column of amounts would not
    // add up to the total printed below it.
    getPackageBookingCourts(bookingData).forEach((court, idx) => {
      rows.push({
        date: bookingData.date,
        court,
        // The time and price belong to the booking, not to each court, so
        // they're printed once however many courts the package takes over.
        label: idx === 0 ? describePackageBookingTime(bookingData) : '',
        amount: idx === 0 ? getPackageBookingPrice(bookingData) : null,
      });
    });
  } else {
    dateGroups.forEach(group => {
      const timeSlots = group.timeSlots || {};
      Object.keys(timeSlots)
        .sort((a, b) => parseTimeSlotStartHour(a) - parseTimeSlotStartHour(b))
        .forEach(timeSlot => {
          (timeSlots[timeSlot] || []).forEach(court => {
            // group.date is passed through so day-scoped rate bands price each
            // date correctly (see getCourtRate in bookingUtils.js).
            const amount = typeof getCourtRate === 'function'
              ? getCourtRate(config, court, timeSlot, null, group.date)
              : null;
            rows.push({ date: group.date, court, timeSlot, amount });
          });
        });
    });
  }

  const tableTop = 216;
  const rowH = 27;
  const colDate = marginX + 10;
  const colCourt = marginX + 135;
  const colTime = marginX + 290;
  const colAmountRight = rightX - 10;

  page.rect(marginX, tableTop, rightX - marginX, rowH, GREEN);
  page.text(colDate, tableTop + 18, 'DATE', { size: 9.5, bold: true, color: WHITE });
  page.text(colCourt, tableTop + 18, 'COURT / ROOM', { size: 9.5, bold: true, color: WHITE });
  page.text(colTime, tableTop + 18, 'TIME', { size: 9.5, bold: true, color: WHITE });
  page.text(colAmountRight, tableTop + 18, 'AMOUNT', { size: 9.5, bold: true, color: WHITE, align: 'right' });

  let y = tableTop + rowH;
  rows.forEach((row, i) => {
    if (i % 2 === 1) page.rect(marginX, y, rightX - marginX, rowH, GREEN_LIGHT);
    const textY = y + 18;
    page.text(colDate, textY, formatShortDate(row.date), { size: 10, color: INK });

    const badgeLabel = getCourtLabel(config, row.court);
    const badgeW = textWidthPt(badgeLabel, false, 9.5) + 16;
    page.rect(colCourt, y + 5, badgeW, rowH - 10, BADGE_BG);
    page.text(colCourt + 8, textY - 1, badgeLabel, { size: 9.5, bold: true, color: BADGE_TEXT });

    // A package row carries its own pre-formatted label ("3:00 PM – 7:00 PM ·
    // 4 hrs") rather than a slot to expand.
    page.text(colTime, textY, row.label !== undefined ? row.label : formatTimeSlotRange(row.timeSlot), { size: 10, color: INK_SOFT });
    page.text(colAmountRight, textY, row.amount != null ? formatMoney(row.amount, config) : '-', { size: 10, bold: true, color: INK, align: 'right' });
    y += rowH;
  });

  if (bookingData.bookingFee) {
    page.rect(marginX, y, rightX - marginX, rowH, GREEN_LIGHT);
    // Per-hour fees need their arithmetic shown — a lone "Processing fee ₱30"
    // beside a ₱5 configured rate reads like a mistake. Empty for a flat
    // fee, where the amount already says everything.
    const feeBreakdown = describeBookingFee(config, dateGroups);
    page.text(colDate, y + 18, feeBreakdown ? `Processing fee (${feeBreakdown})` : 'Processing fee', { size: 10, color: INK_SOFT });
    page.text(colAmountRight, y + 18, formatMoney(bookingData.bookingFee, config), { size: 10, bold: true, color: INK, align: 'right' });
    y += rowH;
  }

  // Total row(s). A partial payment (the guest ticked "Partial payment" at
  // checkout) prints the full total, what was paid and the remaining
  // balance — the same figures as the confirmation screen and the email.
  const breakdown = getPaymentBreakdown(bookingData, config);
  if (breakdown) {
    const balanceColor = hexToPdfColor(GUEST_STATUS_COLORS.pending);
    page.rect(marginX, y, rightX - marginX, rowH * 3, GREEN_LIGHT);
    page.text(colTime, y + 18, PAYMENT_SUMMARY_TOTAL_LABEL, { size: 10, color: INK_SOFT });
    page.text(colAmountRight, y + 18, formatMoney(breakdown.total, config), { size: 10, bold: true, color: INK, align: 'right' });
    y += rowH;
    page.text(colTime, y + 18, `${PAYMENT_SUMMARY_PAID_LABEL} (${describePartialPaymentTag(breakdown)})`, { size: 11, bold: true, color: INK });
    page.text(colAmountRight, y + 18, formatMoney(breakdown.amountPaid, config), { size: 12, bold: true, color: INK, align: 'right' });
    y += rowH;
    page.text(colTime, y + 18, PAYMENT_SUMMARY_BALANCE_LABEL, { size: 10, bold: true, color: balanceColor });
    page.text(colAmountRight, y + 18, formatMoney(breakdown.balance, config), { size: 10, bold: true, color: balanceColor, align: 'right' });
    y += rowH;
  } else {
    page.rect(marginX, y, rightX - marginX, rowH, GREEN_LIGHT);
    page.text(colTime, y + 18, PAYMENT_SUMMARY_TOTAL_LABEL, { size: 11, bold: true, color: INK });
    page.text(colAmountRight, y + 18, formatMoney(bookingData.paymentAmount, config), { size: 12, bold: true, color: INK, align: 'right' });
    y += rowH;
  }

  // ---- Important note ----
  // Same look as the site's amber "Please note" callout: tinted box, solid
  // left bar, bold title, wrapped body.
  const notePadX = 14;
  const noteBarW = 4;
  const noteBodySize = 9.5;
  const noteLineH = 13;
  const noteTextX = marginX + noteBarW + notePadX;
  const noteLines = wrapText(BOOKING_REFERENCE_NOTE_MESSAGE, rightX - noteTextX - notePadX, { size: noteBodySize });
  const noteTop = y + 18;
  const noteH = 14 + 12 + 6 + noteLines.length * noteLineH + 10;
  const noteText = hexToPdfColor(BOOKING_REFERENCE_NOTE_COLORS.text);
  page.rect(marginX, noteTop, rightX - marginX, noteH, hexToPdfColor(BOOKING_REFERENCE_NOTE_COLORS.background));
  page.rect(marginX, noteTop, noteBarW, noteH, hexToPdfColor(BOOKING_REFERENCE_NOTE_COLORS.border));
  page.text(noteTextX, noteTop + 24, BOOKING_REFERENCE_NOTE_TITLE, { size: 11, bold: true, color: noteText });
  noteLines.forEach((line, i) => {
    page.text(noteTextX, noteTop + 24 + 6 + noteLineH * (i + 1), line, { size: noteBodySize, color: noteText });
  });
  y = noteTop + noteH;

  // ---- Footer ----
  // Opening hours are an admin-authored Court Details card now rather than
  // a dedicated field, so this picks out whichever card is about hours by
  // title (same loose match as LandingPage.jsx) and omits the footer
  // segment entirely when there isn't one.
  const hoursDetail = (config?.courtDetails || []).find(d => /hour/i.test(d?.title || '') && (d?.description || '').trim());
  const footerParts = [courtName, config?.locationText, hoursDetail ? `Open ${hoursDetail.description} daily` : null].filter(Boolean);
  page.text(marginX, y + 30, footerParts.join('  ·  '), { size: 9, color: GREEN_MUTED });

  return pageToPdfBlob(page);
}

/**
 * Builds the booking reference PDF and triggers a browser download.
 * Async because the club logo has to be decoded and re-encoded first.
 * @param {object} bookingData - the submitted booking
 * @param {object} config - live club config
 * @param {Function} getCourtRate - bookingUtils.getCourtRate
 * @returns {Promise<void>}
 */
export async function downloadBookingReferencePdf(bookingData, config, getCourtRate) {
  const headerBgHex = pickHex(config?.themeHeaderBackgroundColor, DEFAULT_THEME_COLORS.headerBackground);
  const logo = await loadImageAsJpeg(config?.logoUrl, { background: headerBgHex });
  const blob = buildBookingReferencePdfBlob(bookingData, config, getCourtRate, logo);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${bookingData?.confirmationId || 'booking-reference'}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
